import ActiveFilters from '@dc/ui-library/src/organisms/ActiveFilters/ActiveFilters';
import Icon from '@dc/ui-library/src/molecules/Icon/Icon';
import React, { PureComponent, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import Filter from './Filter';
import FilterPriceRange from './FilterPriceRange';
import FilterLocationSearch from './presentionalComponents/FilterLocationSearch';
import FiltersToggleButton from './FiltersToggleButton';
import { toggleXhrErrorWarning } from '../../../../../appState/ducks/listingPageSetup';
import {
  resetAllFilters,
  toggleCheckboxOption
} from '../../../../../appState/ducks/listingPageFilters';
import { filterTypes, hasActiveFilter, transformForActiveFilters } from './filterHelpers';
import { showPageWrapper, hidePageWrapper } from '../../../PageWrapper';
import FiltersMobileButtons from './FiltersMobileButtons';
import { filtersPropTypes } from './propTypes';
import renderLink from './presentionalComponents/LinkWrapper';

class FiltersContainerTabletMobile extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      containerOpened: false,
      selectedFilter: null
    };

    this.animationDuration = 500;
    this.toggleContainer = this.toggleContainer.bind(this);
    this.hideContainer = this.hideContainer.bind(this);
    this.setSelectedFilter = this.setSelectedFilter.bind(this);
    this.onRemoveActiveFilter = this.onRemoveActiveFilter.bind(this);
  }

  componentWillUnmount() {
    if (this.state.containerOpened) {
      showPageWrapper();
    }
  }

  onRemoveActiveFilter(data) {
    this.props.toggleCheckboxOption({
      ...data,
      fetchNewData: true
    });
  }

  setSelectedFilter(selected) {
    this.setState({
      selectedFilter: selected
    });
  }

  hideContainer() {
    if (this.state.containerOpened) {
      this.toggleContainer();
    }
    this.setSelectedFilter(null);
  }

  toggleContainer(e) {
    if (e) {
      e.preventDefault();
    }
    this.setState((state) => {
      if (state.containerOpened) {
        showPageWrapper(this.animationDuration);
      } else {
        this.props.toggleXhrErrorWarning(false);
        hidePageWrapper();
      }

      return { containerOpened: !state.containerOpened };
    });
  }

  renderSingleOpenedFilter() {
    const isOpened = true;
    return this.renderFilter(
      this.props.filters.find((filter) => this.state.selectedFilter === filter.name),
      isOpened
    );
  }

  renderFilter(filter, openedFilter) {
    const attributes = {
      key: `${filter.name}${+openedFilter}`,
      openedFilter: openedFilter,
      setSelectedFilter: this.setSelectedFilter,
      deviceType: this.props.deviceType,
      ...filter
    };

    if (filter.type === 'price') {
      return (
        <FilterPriceRange
          {...attributes}
        />
      );
    }
    if (filter.type === filterTypes.location) {
      return (
        <Filter
          {...attributes}
        >
          {this.props.locationServiceLoaded && this.props.locationSearchEnabled &&
            <FilterLocationSearch mobileOrTablet />}
        </Filter>
      );
    }
    return <Filter {...attributes} />;
  }

  renderFiltersList() {
    const isOpened = false;
    return this.props.filters.map((filter) => this.renderFilter(filter, isOpened));
  }

  render() {
    const { props } = this;

    return (
      <>
        {this.state.containerOpened && this.props.pendingProductsRequest && (
          <div className={'dc-filter-container__loader'}>
            <Icon icon={'Spinner'} spin />
          </div>
        )}
        <CSSTransition
          classNames={'dc-filter-container'}
          timeout={this.animationDuration}
          in={this.state.containerOpened}
        >
          <div
            className={'dc-filter-container'}
            data-is-loading={this.props.pendingProductsRequest}
            key={'filters-container'}
          >
            {this.state.selectedFilter === null &&
            hasActiveFilter(props, this.state.selectedFilter) && (
            <ActiveFilters
              resetFilters={props.resetAllFilters}
              handleRemoveFilter={this.onRemoveActiveFilter}
              displayResetButton={false}
              filters={transformForActiveFilters(props.filters)}
              filterTypes={filterTypes}
            />
            )}

            {this.state.selectedFilter === null && (
            <header className={'dc-filter-container__header'}>
              <span className={'dc-filter-container__header--bold'}>
                Filter by:
              </span>
              {props.label}
            </header>
            )}

            <div className={'dc-filter-container__body'}>
              {this.state.selectedFilter === null
                ? [
                  this.props.tariff && renderLink(this.props.tariff, 'mobile'),
                  this.renderFiltersList()
                ]
                : this.renderSingleOpenedFilter()}
            </div>

            <FiltersMobileButtons
              key={this.state.selectedFilter || 'none'}
              setSelectedFilter={this.setSelectedFilter}
              disabled={!hasActiveFilter(props, this.state.selectedFilter)}
              selectedFilter={this.state.selectedFilter}
              hideContainer={this.hideContainer}
              productsPostXhrError={this.props.xhrDisplayWarning && this.props.productsPostXhrError}
              noResults={!this.props.categoryProductCount}
            />
          </div>
        </CSSTransition>
        <FiltersToggleButton onClick={this.toggleContainer} />
      </>
    );
  }
}

FiltersContainerTabletMobile.displayName = 'FiltersContainerTabletMobile';

FiltersContainerTabletMobile.propTypes = {
  tariff: PropTypes.shape(),
  resetAllFilters: PropTypes.func,
  toggleCheckboxOption: PropTypes.func,
  toggleXhrErrorWarning: PropTypes.func,
  deviceType: PropTypes.oneOf(['wide', 'desktop', 'tablet', 'mobile']).isRequired,
  label: PropTypes.string.isRequired,
  xhrDisplayWarning: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]).isRequired,
  pendingProductsRequest: PropTypes.bool,
  filters: filtersPropTypes.isRequired,
  productsPostXhrError: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  categoryProductCount: PropTypes.number.isRequired,
  locationServiceLoaded: PropTypes.bool,
  locationSearchEnabled: PropTypes.bool
};

FiltersContainerTabletMobile.defaultProps = {
  tariff: null,
  resetAllFilters: () => {},
  toggleCheckboxOption: () => {},
  toggleXhrErrorWarning: () => {},
  pendingProductsRequest: false,
  productsPostXhrError: null,
  locationServiceLoaded: false,
  locationSearchEnabled: false
};

function mapStateToProps(state) {
  return {
    pendingProductsRequest: state.listingPage.pageSetup.xhrStatus.pendingProductsRequest,
    productsPostXhrError: state.listingPage.pageSetup.xhrStatus.productsPostXhrError,
    categoryProductCount: state.listingPage.pagination.itemsNumberPerPage.categoryProductCount,
    xhrDisplayWarning: state.listingPage.pageSetup.xhrStatus.xhrDisplayWarning,
    locationServiceLoaded: !state.listingPage.locationSearch.locationServiceLoading,
    locationSearchEnabled: state.listingPage.pageSetup.islocationSearchEnabled
  };
}

export default connect(mapStateToProps, {
  toggleXhrErrorWarning,
  resetAllFilters,
  toggleCheckboxOption
})(FiltersContainerTabletMobile);
